import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Home, Loader, AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../lib/api/client';

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('order_id');
    const tierParam = searchParams.get('tier');
    const statusParam = searchParams.get('status'); // from Cashfree return_url

    const [state, setState] = useState('verifying'); // verifying | success | failed
    const [message, setMessage] = useState('');
    const [newTier, setNewTier] = useState(tierParam || '');
    const [invoiceUrl, setInvoiceUrl] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const verify = async (retryCount = 0) => {
            if (!orderId) {
                setState('failed');
                setMessage('No order information found.');
                return;
            }

            // If Cashfree already told us it failed via return URL
            if (statusParam && statusParam.toUpperCase() === 'FAILED') {
                setState('failed');
                setMessage('Payment was not successful. No charges were made. Please try again.');
                return;
            }

            try {
                const res = await apiClient.post('/api/v1/payments/verify', { order_id: orderId });
                if (res.data.success) {
                    setState('success');
                    setNewTier(res.data.new_tier || tierParam || '');
                    setInvoiceUrl(res.data.invoice_url);
                    setMessage(res.data.message || 'Your plan has been upgraded!');
                } else {
                    // Retry once after 2 seconds if first attempt says failed
                    if (retryCount === 0) {
                        setTimeout(() => verify(1), 2000);
                        return;
                    }
                    setState('failed');
                    setMessage(res.data.message || 'Payment verification failed.');
                }
            } catch (err) {
                console.error('Verify error:', err);
                // Retry once after 2 seconds on network/server error
                if (retryCount === 0) {
                    setTimeout(() => verify(1), 2000);
                    return;
                }
                setState('failed');
                setMessage(err?.response?.data?.detail || 'Could not verify payment. Please contact support.');
            }
        };

        verify();
    }, [orderId, statusParam]);

    const handleDownloadInvoice = async () => {
        if (!orderId) return;
        setDownloading(true);
        try {
            const res = await apiClient.get(`/api/v1/payments/invoice/${orderId}`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `TeachGenie-Invoice-${orderId}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Invoice download error:', err);
        } finally {
            setDownloading(false);
        }
    };

    const tierLabel = (t) => {
        const map = { silver: 'Silver', gold: 'Gold', platinum: 'Platinum', institutional: 'Institutional' };
        return map[t?.toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Upgraded');
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #050505 0%, #0f1a0f 50%, #050505 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: '"Outfit", sans-serif',
            padding: '40px 20px',
        }}>
            <Helmet>
                <title>
                    {state === 'success' ? 'Payment Successful | TeachGenie' :
                     state === 'failed' ? 'Payment Failed | TeachGenie' :
                     'Verifying Payment | TeachGenie'}
                </title>
            </Helmet>

            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: state === 'success'
                        ? '1.5px solid rgba(5,150,105,0.5)'
                        : state === 'failed'
                        ? '1.5px solid rgba(239,68,68,0.4)'
                        : '1.5px solid rgba(255,255,255,0.1)',
                    borderRadius: '28px',
                    padding: '60px 50px',
                    maxWidth: '520px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: state === 'success'
                        ? '0 0 60px rgba(5,150,105,0.12)'
                        : '0 0 40px rgba(0,0,0,0.4)',
                }}
            >
                {/* Icon */}
                <div style={{ marginBottom: '28px' }}>
                    {state === 'verifying' && (
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                            style={{ display: 'inline-block' }}
                        >
                            <Loader size={56} color="#f59e0b" strokeWidth={1.5} />
                        </motion.div>
                    )}
                    {state === 'success' && (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                        >
                            <CheckCircle size={72} color="#10b981" strokeWidth={1.5} />
                        </motion.div>
                    )}
                    {state === 'failed' && (
                        <AlertTriangle size={64} color="#ef4444" strokeWidth={1.5} />
                    )}
                </div>

                {/* Title */}
                <h1 style={{
                    fontSize: '28px',
                    fontWeight: '900',
                    color: state === 'success' ? '#10b981' : state === 'failed' ? '#ef4444' : '#f59e0b',
                    marginBottom: '12px',
                    letterSpacing: '-0.5px',
                }}>
                    {state === 'verifying' && 'Confirming your payment…'}
                    {state === 'success' && '🎉 Payment Successful!'}
                    {state === 'failed' && 'Payment Failed'}
                </h1>

                {/* Subtitle */}
                <p style={{ fontSize: '16px', color: '#d1d5db', marginBottom: '32px', lineHeight: '1.6' }}>
                    {state === 'verifying' && 'Please wait while we confirm your transaction with the payment gateway.'}
                    {state === 'success' && (
                        <>
                            Your account has been upgraded to the{' '}
                            <strong style={{ color: '#f59e0b' }}>{tierLabel(newTier)} Plan</strong>.{' '}
                            {message}
                        </>
                    )}
                    {state === 'failed' && message}
                </p>

                {/* Order ID chip */}
                {orderId && state !== 'verifying' && (
                    <div style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        padding: '10px 16px',
                        marginBottom: '32px',
                        fontSize: '13px',
                        color: '#9ca3af',
                        wordBreak: 'break-all',
                    }}>
                        Order ID: <strong style={{ color: '#d1d5db' }}>{orderId}</strong>
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {state === 'success' && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleDownloadInvoice}
                            disabled={downloading}
                            style={{
                                padding: '16px',
                                borderRadius: '14px',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: '#fff',
                                fontWeight: '900',
                                fontSize: '16px',
                                cursor: downloading ? 'not-allowed' : 'pointer',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                boxShadow: '0 8px 24px rgba(16,185,129,0.25)',
                                opacity: downloading ? 0.7 : 1,
                            }}
                        >
                            {downloading
                                ? <><Loader size={18} /> Downloading…</>
                                : <><Download size={18} /> Download Invoice (PDF)</>
                            }
                        </motion.button>
                    )}

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/generator')}
                        style={{
                            padding: '16px',
                            borderRadius: '14px',
                            background: state === 'success' ? 'rgba(255,255,255,0.07)' : '#f59e0b',
                            color: state === 'success' ? '#d1d5db' : '#000',
                            fontWeight: '900',
                            fontSize: '16px',
                            cursor: 'pointer',
                            border: state === 'success' ? '1px solid rgba(255,255,255,0.1)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                        }}
                    >
                        <Home size={18} />
                        {state === 'success' ? 'Start Generating Lessons' : 'Go to Dashboard'}
                    </motion.button>

                    {state === 'failed' && (
                        <button
                            onClick={() => navigate('/pricing')}
                            style={{
                                padding: '14px',
                                borderRadius: '14px',
                                background: 'transparent',
                                color: '#9ca3af',
                                fontWeight: '700',
                                fontSize: '14px',
                                cursor: 'pointer',
                                border: '1px solid rgba(255,255,255,0.1)',
                            }}
                        >
                            Try Again
                        </button>
                    )}
                </div>

                {/* Support note */}
                <p style={{ marginTop: '32px', fontSize: '12px', color: '#4b5563' }}>
                    For any payment issues, contact{' '}
                    <a href="mailto:support@teachgenie.ai" style={{ color: '#6b7280', textDecoration: 'underline' }}>
                        support@teachgenie.ai
                    </a>
                </p>
            </motion.div>
        </div>
    );
};

export default PaymentSuccess;
